import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { isNullOrUndefined } from '@sla-ng/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ICostCenterCostDistribution } from '../../../../../../models/cost-center-cost-distribution.model';
import { IRebookingDialogLayout } from '../../../../../../models/layout/rebooking-dialog-layout.model';
import { ICostCenterCostRebookingState } from '../../../../../../services/facades/cost-accounting/cost-center-cost-rebooking-state.model';
import { CostCenterCostRebookingFacade } from '../../../../../../services/facades/cost-accounting/cost-center-cost-rebooking.facade';
import { RebookingLayoutService } from '../../../../../../services/layout/rebooking-layout.service';

@Component({
    templateUrl: './rebooking-dialog.component.html',
    styleUrls: [ './rebooking-dialog.component.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RebookingDialogComponent implements OnInit {

    public rebookingState$: Observable<ICostCenterCostRebookingState>;
    public layout: IRebookingDialogLayout;
    public initialSourceData: BehaviorSubject<{ costCenter: string, costType: string, value: number }> = new BehaviorSubject(undefined);

    public rebookingValueCt: BehaviorSubject<number> = new BehaviorSubject(0);
    public targetValueBefore: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    public targetSelected: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private targetFormGroup$: BehaviorSubject<FormGroup> = new BehaviorSubject(undefined);

    constructor(private myRebookingFacade: CostCenterCostRebookingFacade,
                private myRebookingLayoutService: RebookingLayoutService,
                private myMatDialogRef: MatDialogRef<RebookingDialogComponent>,
                @Inject(MAT_DIALOG_DATA) public data: ICostCenterCostDistribution) {
    }

    public ngOnInit(): void {


        this.layout = this.myRebookingLayoutService.getRebookingDialogLayout();
        this.rebookingState$ = this.myRebookingFacade.subState();
        this.initialSourceData.next({
            costCenter: this.data.costCenter.name,
            costType: this.data.costType.name,
            value: 0,
        });
    }

    public onSourceFormgroupChange(formGroup: FormGroup): void {

        formGroup.controls[ 'value' ].valueChanges.subscribe((rebookingEuroValue: number) => {

            // The account balance after subtraction must not be smaller than zero!
            // Set the rebookingEuroValue to the accounts balance to prevent that
            if (rebookingEuroValue > this.data.costCt / 100) {
                formGroup.controls[ 'value' ].patchValue(this.data.costCt / 100);
            } else {
                this.rebookingValueCt.next(rebookingEuroValue * 100);
            }
        });
    }

    public onTargetFormgroupChange(formGroup: FormGroup): void {
        this.targetFormGroup$.next(formGroup);

        // Initialize them with undefined. Workaround because ui-material initializes them with empty string '' which leads to problems with typing...
        formGroup.patchValue({
            targetCostCenterId: undefined,
            secondaryCostTypeId: undefined,
        });

        formGroup.valueChanges.subscribe((values: { targetCostCenterId: number, secondaryCostTypeId: number }) => {

            // Recalculate balance for target
            if (!isNullOrUndefined(values.targetCostCenterId) && !isNullOrUndefined(values.secondaryCostTypeId)) {
                this.targetSelected.next(true);
                const foundDistribution: ICostCenterCostDistribution = this.myRebookingFacade.findExistingCostCenterCostDistribution(values.targetCostCenterId, values.secondaryCostTypeId);
                const targetValueBeforeCt: number = isNullOrUndefined(foundDistribution) ? 0 : foundDistribution.costCt;
                this.targetValueBefore.next(targetValueBeforeCt);
            }
        });

    }

    public save(): void {

        const rebookingValueCt: number = this.rebookingValueCt.value;
        const rebookingTarget: { targetCostCenterId: number, secondaryCostTypeId: number } = this.targetFormGroup$.value.getRawValue();

        void this.myRebookingFacade.redistributeCostCenterCost(this.data.id,
            {
                targetCostCenterId: rebookingTarget.targetCostCenterId,
                targetCostTypeId: rebookingTarget.secondaryCostTypeId,
                valueCt: rebookingValueCt
            });

        this.myMatDialogRef.close(true);

    }

    public close(): void {
        this.myMatDialogRef.close(false);
    }
}
